// @ts-strict-ignore
/** @jsx jsx */
import { jsx } from 'theme-ui'
import RichTextBlock from '@uswitch/ev-components/lib/components/modules/rich-text-block'
import Icon from '../../../atoms/icon'
import { useEntities } from '@uswitch/ev-utils/context/selectors'
import StatSnippet from '../stat-snippet'
import { removeWhitespaceAndSpecialChars } from '@h/commons'

export const moneyHeadingStyles = {
  'h1, h2, h3': {
    color: '#924a8b',
    lineHeight: '120%',
    fontWeight: '600',
  },
  h2: {
    marginBottom: '24px',
    '@media screen and (min-width: 768px)': { marginBottom: '32px' },
  },
  'h1, h2': { fontSize: '30px', '@media screen and (min-width: 768px)': { fontSize: '36px' } },
  h3: {
    fontSize: '24px',
    '@media screen and (min-width: 768px)': { fontSize: '30px' },
  },
}

export const uswitchHeadingStyles = {
  'h1, h2, h3': {
    color: '#141424',
    lineHeight: '125%',
    fontWeight: '600',
    marginBottom: '24px',
    '@media screen and (min-width: 768px)': {
      marginBottom: '32px',
    },
  },
  'h1, h2': {
    fontSize: '32px',
    '@media screen and (min-width: 768px)': {
      fontSize: '40px',
    },
  },
  h3: {
    fontSize: '24px',
    '@media screen and (min-width: 768px)': {
      fontSize: '28px',
    },
  },
}

export const moneyRichTextBlockStyles = {
  ...moneyHeadingStyles,
  'p, a, a:visited, a:active, a:hover, li': {
    fontSize: '16px',
    lineHeight: '150%',
    '@media screen and (min-width: 768px)': { fontSize: '20px' },
  },
  p: {
    color: '#34454E',
  },
  'a, a:visited, a:active, a:hover': {
    textDecoration: 'underline',
    color: '#924a8b',
    cursor: 'pointer',
  },
  li: {
    color: '#34454E',
    '::before': {
      color: '#924a8b',
    },
  },
  sup: {
    color: '#924a8b',
    fontSize: '8px',
    lineHeight: '150%',
    '@media screen and (min-width: 768px)': { fontSize: '12px' },
  },
  blockquote: {
    background: 'none',
    padding: 'inherit',
    '> p': {
      fontFamily: 'Nunito',
      color: '#DB4D75',
      fontSize: '24px',
      fontWeight: '600',
      lineHeight: '120%',
      borderLeft: 'none',
      paddingLeft: 0,
      '@media screen and (min-width: 768px)': { fontSize: '30px' },
    },
  },
}

export const uswitchRichTextBlockStyles = {
  ...uswitchHeadingStyles,
  'p, a, a:visited, a:active, a:hover, li': {
    fontSize: '16px',
    lineHeight: '150%',
    '@media screen and (min-width: 768px)': { fontSize: '18px' },
  },
  p: {
    color: '#434350',
  },
  'a, a:visited, a:active, a:hover': {
    textDecoration: 'underline',
    color: '#141424',
    cursor: 'pointer',
    borderBottom: 'none',
  },
  li: {
    marginBottom: '8px',
    color: '#434350',
    '::before': {
      marginBottom: '8px',
      color: '#141424',
    },
  },
  sup: {
    color: '#141424',
    fontSize: '8px',
    lineHeight: '150%',
    '@media screen and (min-width: 768px)': { fontSize: '12px' },
  },
  blockquote: {
    background: 'none',
    padding: 'inherit',
    '> p': {
      fontFamily:
        'Helvetica Now Display, Helvetica Neue, Helvetica, Arial, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen-Sans, Ubuntu, Cantarell, Fira Sans, Droid Sans,sans-serif',
      color: '#141424',
      fontSize: '24px',
      fontWeight: '600',
      lineHeight: '120%',
      borderLeft: 'none',
      paddingLeft: 0,
      '@media screen and (min-width: 768px)': { fontSize: '30px' },
    },
  },
}

export const moneyQuoteStyles = {
  fontFamily: 'Nunito',
  color: '#DB4D75',
  fontSize: '24px',
  fontWeight: '600',
  lineHeight: '120%',
  '@media screen and (min-width: 768px)': { fontSize: '30px' },
}

export const uswitchQuoteStyles = {
  p: {
    fontFamily:
      'Helvetica Now Display, Helvetica Neue, Helvetica, Arial, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen-Sans, Ubuntu, Cantarell, Fira Sans, Droid Sans,sans-serif',
    color: '#141424',
    fontSize: '24px',
    fontWeight: '700',
    lineHeight: '125%',
    '@media screen and (min-width: 768px)': { fontSize: '32px' },
  },
}

const SingleColumnRichText = (props) => {
  const { content: document, heading = undefined, brand, name } = props
  let richBlock = document
  let blockquote
  let statSnipet
  if (document) {
    const { content } = document
    let newContent = JSON.parse(JSON.stringify(content))

    blockquote = content
      .slice()
      .reverse()
      .find((c) => c.nodeType === 'blockquote')

    if (blockquote) {
      newContent = newContent.filter((c) => c.nodeType !== 'blockquote')
    }

    const embededEntries = content.filter((c) => c.nodeType === 'embedded-entry-block')
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const entries = useEntities(embededEntries.map((e) => e.data.target))
    statSnipet = entries.find((c) => c.variant === 'stat-snippet')

    if (statSnipet) {
      newContent = newContent.filter((c) => c?.data?.target?.sys?.id !== statSnipet.sys.id)
    }

    richBlock = { ...document, content: newContent }
  }

  return (
    <div className='SingleColumnRichTextNew' id={removeWhitespaceAndSpecialChars(name)}>
      {heading && (
        <RichTextBlock
          {...props}
          content={heading}
          style={brand === 'money' ? moneyHeadingStyles : uswitchHeadingStyles}
        />
      )}
      {richBlock && (
        <div className='SingleColumnRichTextNew__container'>
          <div className='SingleColumnRichTextNew__container__columnOne'>
            <RichTextBlock
              {...props}
              content={richBlock}
              style={brand === 'money' ? moneyRichTextBlockStyles : uswitchRichTextBlockStyles}
            />
          </div>
          {(blockquote || statSnipet) && (
            <div className='SingleColumnRichTextNew__container__columnTwo'>
              {blockquote && (
                <div className='SingleColumnRichTextNew__container__columnTwo__quote'>
                  <div>
                    <Icon glyph={'quotemarks-stroke'} size={45} desktopSize={65} color='#34454E' />
                    <RichTextBlock
                      {...props}
                      content={blockquote}
                      style={brand === 'money' ? moneyQuoteStyles : uswitchQuoteStyles}
                    />
                  </div>
                </div>
              )}
              {statSnipet && <StatSnippet {...statSnipet} />}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SingleColumnRichText
